import { useState, useEffect, useCallback } from 'react'
import {
  typedCollection,
  query,
  orderBy,
  onSnapshot,
} from '@/lib/firebase/firestore'
import { formatUserFacingError } from '@/lib/errors'
import { notificationsService } from '../services/notifications.service'
import type { NotificationDocument } from '@/types'

export function useNotifications() {
  const [notifications, setNotifications] = useState<Array<NotificationDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const col = typedCollection<NotificationDocument>('notifications')
    const q = query(col, orderBy('createdAt', 'desc'))

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }))
        setNotifications(docs)
        setLoading(false)
      },
      (err) => {
        setError(formatUserFacingError(
          err,
          'We could not keep your notifications up to date. Please try again.',
          'notifications/subscribe',
        ))
        setLoading(false)
      }
    )

    return unsubscribe
  }, [])

  const unreadCount = useCallback(
    (uid: string) =>
      notifications.filter((n) => !n.resolved && !n.readBy.includes(uid)).length,
    [notifications]
  )

  const markRead = useCallback(async (id: string, uid: string) => {
    await notificationsService.markRead(id, uid)
  }, [])

  const resolve = useCallback(async (id: string, resolvedByUid: string) => {
    await notificationsService.resolve(id, resolvedByUid)
  }, [])

  return { notifications, loading, error, unreadCount, markRead, resolve }
}
